import { format } from "date-fns";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
} from "firebase/firestore";
import { CalendarDays } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import blueSkyTransparentIcon from "../../../assets/bluesky-transparent-icon.png";
import editIcon from "../../../assets/caseManagement/header/edit-icon.png";
import anchorIcon from "../../../assets/caseManagement/sidebar/anchor-icon.png";
import anchorLastIndexIcon from "../../../assets/caseManagement/sidebar/anchor-last-index-icon.png";
import dashboardHighlightedIcon from "../../../assets/caseManagement/sidebar/dashboard-highlighted-icon.png";
import dashboardIcon from "../../../assets/caseManagement/sidebar/dashboard-icon.png";
import survivorsHighlightedIcon from "../../../assets/caseManagement/sidebar/survivors-highlighted-icon.png";
import survivorsIcon from "../../../assets/caseManagement/sidebar/survivors-icon.png";
import ScreeningNavbar from "../../../components/ScreeningNavbar";
import Modal from "../../../components/Modal";
import { useDisasterObjContext } from "../../../context/DisasterObjContext";
import { useSidebarContext } from "../../../context/SidebarContext";
import { db } from "../../../firebase";
import citizenProfileService from "../../../service/citizen/CitizenProfile";

const employeeDetails = JSON.parse(sessionStorage.getItem("employeeDetails"));

export const screeningSidebarItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: (active) => (active ? dashboardHighlightedIcon : dashboardIcon),
  },
  {
    id: "survivors",
    label: "Survivors",
    icon: (active) => (active ? survivorsHighlightedIcon : survivorsIcon),
    subItems: [
      {
        id: "irs",
        label: "Background",
        subItems: [
          {
            id: "clientinformation",
            label: "Client Information",
          },
          {
            id: "alternatecontact",
            label: "Alternate Contact",
          },
          {
            id: "householddemographics",
            label: "Household Demographics",
          },
          {
            id: "householdmembers",
            label: "Household Members",
          },
          {
            id: "pets",
            label: "Pets",
          },
          {
            id: "damagedescription",
            label: "Damage Description",
          },
        ],
      },
      {
        id: "accounting",
        label: "Accounting",
      },
      {
        id: "unmetneeds",
        label: "Unmet Needs",
        subItems: [
          {
            id: "immediateneedssection",
            label: "Immediate Needs",
          },
          {
            id: "personalpropertysection",
            label: "Personal Property",
          },
          {
            id: "housingconstructionsection",
            label: "Housing & Construction",
          },
          {
            id: "servicereferralssection",
            label: "Service & Referrals",
          },
          {
            id: "advocacysection",
            label: "Advocacy",
          },
        ],
      },
    ],
  },
];

const ScreeningLayout = ({ children, tab }) => {
  const { tab: selectedTab, subtab: selectedSubTab } = useParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [
    {
      clientName,
      caseNumber,
      birthDate,
      mobileNumber,
      email,
      fema_number,
      disaster_number,
      responseDate,
    },
    setCaseDetails,
  ] = useState({
    clientName: "",
    caseNumber: "",
    birthDate: "",
    mobileNumber: "",
    email: "",
    fema_number: "",
    disaster_number: "",
    responseDate: "",
  });
  // const [toggleSidebar, setToggleSidebar] = useState(true);
  const { toggleSidebar, setToggleSidebar } = useSidebarContext();

  const [toggleCaseDetailsModal, setToggleCaseDetailsModal] = useState(false);

  const [editPersonalInformationModal, setEditPersonalInformationModal] =
    useState(false);

  const { disaster } = useDisasterObjContext();

  const [citizenData, setCitizendata] = useState();

  // const [accessedUserId, setAccessedUserId] = useState();

  const [lastVisitedDisasterSubTab, setLastVisitedDisasterSubTab] = useState(
    sessionStorage.getItem("lastVisitedDisasterSubTab") || "disasterinfo"
  );

  const selectedUserId = sessionStorage.getItem("userId");

  const sessionAccessedUserId = sessionStorage.getItem("accessedUserId");

  const lastVisitedSubTab = sessionStorage.getItem("lastVisitedSubTab");

  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!selectedUserId) return;

      const userDoc = await getDoc(doc(db, "users", selectedUserId));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    };

    fetchUserData();
  }, [selectedUserId]);

  const filteredSidebarItems = useMemo(() => {
    return screeningSidebarItems.filter((item) => {
      // Always show dashboard and survivors/cases
      if (
        item.id === "dashboard" ||
        item.id === "survivors" ||
        item.id === "calendar"
      ) {
        return true;
      }

      // Hide partners section for partner users
      if (item.id === "partners" && userData?.user_role === "partner") {
        return false;
      }

      // Only show Case Management Agency for supervisors/admins who are not partners
      if (item.id === "casemgt") {
        return (
          (employeeDetails?.role === "Case Supervisor" ||
            employeeDetails?.is_admin) &&
          userData?.user_role !== "partner"
        );
      }

      // Filter Disaster section
      if (item.id === "disaster") {
        // If partner, only show overview and disaster_lockbox
        return userData?.user_role !== "partner";
      }

      // Filter Community Contacts
      if (item.id === "communitycontacts") {
        // Only show for center users or specific partner roles
        return userData?.user_role !== "partner";
      }

      // Case Manager section
      if (item.id === "casemanager") {
        // Show for center users and partner supervisors/admins
        return (
          userData?.user_role !== "partner" ||
          employeeDetails?.role === "Case Supervisor" ||
          employeeDetails?.is_admin
        );
      }

      return true;
    });
  }, [userData, employeeDetails]);

  const onNavigate = useCallback(
    (tab, subTab, anchor, activeTab) => () => {
      const searchParams = new URLSearchParams();

      if (activeTab) {
        searchParams.set("tab", activeTab);
      }

      navigate(
        `/screening/${tab}/${
          subTab ||
          (tab === "survivors" ? lastVisitedSubTab || "" : "") ||
          (tab === "disaster" ? lastVisitedDisasterSubTab || "" : "") ||
          "none"
        }?${searchParams.toString()}`
      );

      if (!!anchor) {
        onScrollToAnchor(anchor)();
      }
    },
    [tab, lastVisitedSubTab, lastVisitedDisasterSubTab]
  );

  const onScrollToAnchor = useCallback(
    (anchorId) => () => {
      window.location.hash = anchorId;
      const element = document.getElementById(anchorId);
      if (!!element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    []
  );

  const onVisitSurvivors = useCallback(() => {
    const urlQueryParams = new URLSearchParams();
    urlQueryParams.set("tab", 2);

    navigate(
      `/screening/survivors/${
        lastVisitedSubTab || "irs"
      }/?${urlQueryParams.toString()}`
    );
  }, [lastVisitedSubTab]);

  // const onVisitDisaster = useCallback(() => {
  //   const urlQueryParams = new URLSearchParams();
  //   urlQueryParams.set("tab", 5);

  //   navigate(
  //     `/cencasemanagement/disaster/${
  //       lastVisitedDisasterSubTab || "disasterinfo"
  //     }/?${urlQueryParams.toString()}`
  //   );
  // }, [lastVisitedDisasterSubTab]);

  const fetchName = async () => {
    try {
      const auth = getAuth();
      if (!auth.currentUser?.uid || !disaster) return;

      // Get the accessed user ID from current user's document
      // const userDoc = await getDoc(doc(db, "users", selectedUserId));
      // const userData = userDoc.data();
      // const accessedUserId = userData.accessed_userId;

      if (!sessionAccessedUserId) {
        console.log("No accessed user found");
        return;
      }

      const surveyQuery = query(
        collection(db, `users/${sessionAccessedUserId}/survey_responses`)
      );

      const querySnapshot = await getDocs(surveyQuery);

      const parsedQuerySnapShot = querySnapshot.docs
        .map((doc) => doc.data())
        .find(
          ({ disasters }) =>
            !!disasters.find(({ disaster_id }) => disaster_id === disaster.id)
        );

      if (!!parsedQuerySnapShot) {
        setCaseDetails((prev) => ({
          ...prev,
          caseNumber: parsedQuerySnapShot.case_number,
          fema_number: parsedQuerySnapShot.fema_number,
          disaster_number: parsedQuerySnapShot.disaster_number,
          responseDate: parsedQuerySnapShot.updated_at,
        }));
      }

      // First fetch the default profile data
      const citizenData = await citizenProfileService.getCitizenProfile(
        "users",
        sessionAccessedUserId
      );

      setCitizendata(citizenData);
      // setAccessedUserId(sessionAccessedUserId);

      if (citizenData) {
        // If we have selected disasters, try to fetch disaster-specific address
        if (!!disaster) {
          const impactedAddressRef = doc(
            db,
            `users/${sessionAccessedUserId}/impacted-addresses/${disaster.id}`
          );
          const impactedAddressDoc = await getDoc(impactedAddressRef);

          if (impactedAddressDoc.exists()) {
            // Use disaster-specific address data
            const addressData = impactedAddressDoc.data();
            setCaseDetails((prev) => ({
              ...prev,
              clientName: `${
                addressData.first_name || citizenData.first_name
              } ${addressData.last_name || citizenData.last_name}`,
              email: addressData.email || citizenData.email,
              mobileNumber:
                addressData.mobile_number || citizenData.mobile_number,
              birthDate: addressData.birth_date || citizenData.birth_date,
            }));
          } else {
            // Use default profile data for new disaster entry
            const fullName = `${citizenData.first_name} ${citizenData.last_name}`;
            setCaseDetails((prev) => ({
              ...prev,
              clientName: fullName,
            }));
          }
        } else {
          // Use default profile data when no disaster is selected
          const fullName = `${citizenData.first_name} ${citizenData.last_name}`;
          setCaseDetails((prev) => ({
            ...prev,
            clientName: fullName,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching name:", error);
    }
  };

  useEffect(() => {
    if (disaster && selectedUserId && sessionAccessedUserId) {
      fetchName();
    }
  }, [disaster, selectedUserId, sessionAccessedUserId]);

  const renderSidebar = useMemo(() => {
    return (
      <div className="flex flex-col">
        {filteredSidebarItems.map(({ icon, label, id, subItems }) => {
          const isSelected = selectedTab === id;
          return (
            <div key={id} className="flex flex-col space-y-2">
              <div
                className={`flex cursor-pointer items-center space-x-3 rounded-md p-[12px] ${
                  isSelected ? "bg-[#003460]" : ""
                } ${!toggleSidebar ? "justify-center" : ""}`}
                onClick={onNavigate(id)}
              >
                <div className="h-[20px] w-[20px]">
                  <img
                    src={icon(isSelected)}
                    className="h-full w-full object-contain"
                  />
                </div>
                {toggleSidebar && (
                  <p className={`${isSelected ? "text-white" : ""} text-sm`}>
                    {label}
                  </p>
                )}
              </div>
              {toggleSidebar &&
                !!subItems &&
                !!subItems.length &&
                isSelected &&
                !!selectedSubTab &&
                selectedSubTab !== "none" && (
                  <div className="flex flex-col">
                    {subItems.map(
                      ({ label, id: subItemId, subItems: anchorSub }) => {
                        const isSelectedSubTab = subItemId === selectedSubTab;
                        return (
                          <div className="flex flex-col" key={subItemId}>
                            <div
                              key={subItemId}
                              className={`cursor-pointer rounded-md px-[30px] py-[12px] ${
                                isSelectedSubTab ? "bg-[#FFF3CE]" : ""
                              }`}
                              onClick={() => {
                                onNavigate(
                                  id,
                                  subItemId,
                                  !!anchorSub && !!anchorSub.length
                                    ? anchorSub[0].id
                                    : null,
                                  selectedTab === "survivors" ? 2 : 5
                                )();
                                if (selectedTab === "survivors") {
                                  sessionStorage.setItem(
                                    "lastVisitedSubTab",
                                    subItemId
                                  );
                                } else if (selectedTab === "disaster") {
                                  sessionStorage.setItem(
                                    "lastVisitedDisasterSubTab",
                                    subItemId
                                  );
                                  setLastVisitedDisasterSubTab(subItemId);
                                }
                              }}
                            >
                              <p className="text-sm">{label}</p>
                            </div>
                            <div className="flex flex-col">
                              {!!anchorSub &&
                                !!anchorSub.length &&
                                isSelectedSubTab &&
                                anchorSub.map(
                                  (
                                    { id: anchorId, label: anchorLabel },
                                    k,
                                    arr
                                  ) => {
                                    const lastItemIndex = arr.length - 1;
                                    const isLastItem = k === lastItemIndex;
                                    return (
                                      <div
                                        key={anchorId}
                                        className={`relative flex h-[40px] cursor-pointer items-center space-x-2 rounded-md pl-[25px] pr-[10px] ${
                                          anchorId &&
                                          hash.substring(1) === anchorId
                                            ? "bg-[#F3F3F3]"
                                            : ""
                                        } ${isLastItem === k ? "mb-2" : ""}`}
                                        onClick={onScrollToAnchor(anchorId)}
                                      >
                                        <div className="h-[40px] w-[24px]">
                                          <img
                                            src={
                                              isLastItem
                                                ? anchorLastIndexIcon
                                                : anchorIcon
                                            }
                                            className="h-full w-full object-contain"
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm">
                                            {anchorLabel}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
            </div>
          );
        })}
      </div>
    );
  }, [
    selectedTab,
    screeningSidebarItems,
    filteredSidebarItems,
    selectedSubTab,
    hash,
    toggleSidebar,
    onNavigate,
  ]);

  const EditPersonalInformationModal = ({
    editPersonalInformationModal,
    setEditPersonalInformationModal,
    citizenData,
    accessedUserId,
    disaster,
    fema_number,
    disaster_number,
  }) => {
    const {
      register,
      handleSubmit,
      formState: { errors },
      setValue,
    } = useForm({
      defaultValues: {
        fema_no: "",
        contract_no: "",
      },
    });

    useEffect(() => {
      setValue("fema_no", fema_number || "");
      setValue("contract_no", disaster_number || "");
    }, [fema_number, disaster_number, setValue]);

    const onSubmit = async (data) => {
      try {
        if (!accessedUserId || !disaster) {
          console.error("Missing user ID or disaster information.");
          return;
        }

        const surveyQuery = query(
          collection(db, `users/${accessedUserId}/survey_responses`)
        );

        const querySnapshot = await getDocs(surveyQuery);

        const surveyDoc = querySnapshot.docs.find((doc) =>
          doc.data().disasters.some((d) => d.disaster_id === disaster.id)
        );

        if (!surveyDoc) {
          console.error("No matching survey response found.");
          return;
        }

        const surveyDocRef = doc(
          db,
          `users/${accessedUserId}/survey_responses`,
          surveyDoc.id
        );

        await updateDoc(surveyDocRef, {
          fema_number: data.fema_no,
          disaster_number: data.contract_no,
        });

        console.log("Successfully updated FEMA and Contract number.");
        fetchName();
        setEditPersonalInformationModal(false);
      } catch (error) {
        console.error("Error updating data:", error);
      }
    };

    const handleFormSubmit = () => {
      handleSubmit(onSubmit)();
      fetchName();
    };

    return editPersonalInformationModal ? (
      <Modal
        isOpen={editPersonalInformationModal}
        title={"Edit Personal Information"}
        onClose={() => {
          setEditPersonalInformationModal(false);
        }}
        customOnRightButtonClick={handleFormSubmit}
      >
        <form className="relative flex flex-col gap-5">
          {/* Name */}
          <div className="flex flex-1 flex-row items-center rounded border border-solid border-[#CFCFCF] p-2 px-3 text-gray1-100">
            <div className="flex flex-1 flex-col items-start gap-1">
              <p className="text-[#8F8F8F]">Name</p>
              <p className="text-sm font-normal text-gray1-100">
                {citizenData?.first_name || "N/A"}{" "}
                {citizenData?.last_name || "N/A"}
              </p>
            </div>
          </div>

          {/* Birth Date and Contact No. */}
          <div className="flex w-full flex-row gap-5">
            {/* Date of Birth - Read Only */}
            <div className="flex flex-1 flex-row items-center rounded border border-solid border-[#CFCFCF] p-2 px-3 text-gray1-100">
              <div className="flex flex-1 flex-col items-start gap-1">
                <p className="text-[#8F8F8F]">Date of Birth</p>
                <p className="text-sm font-normal text-gray1-100">
                  {citizenData?.birth_date
                    ? format(new Date(citizenData.birth_date), "MM/dd/yyyy")
                    : "N/A"}
                </p>
              </div>
              <CalendarDays className="text-[#CFCFCF]" />
            </div>

            {/* Contact Number - Read Only */}
            <div className="flex flex-1 flex-row items-center rounded border border-solid border-[#CFCFCF] p-2 px-3 text-gray1-100">
              <div className="flex flex-1 flex-col items-start gap-1">
                <p className="text-[#8F8F8F]">Phone No.</p>
                <p className="text-sm font-normal text-gray1-100">
                  {citizenData?.mobile_number || "N/A"}
                </p>
              </div>
            </div>
          </div>
          {/* Email Address - Read Only */}
          <div className="flex flex-1 flex-row items-center rounded border border-solid border-[#CFCFCF] p-2 px-3 text-gray1-100">
            <div className="flex flex-1 flex-col items-start gap-1">
              <p className="text-[#8F8F8F]">Email Address</p>
              <p className="text-sm font-normal text-gray1-100">
                {citizenData?.email || "N/A"}
              </p>
            </div>
          </div>

          {/* FEMA No. and Disaster No. - Editable */}
          <div className="flex w-full flex-row gap-5">
            {/* FEMA No. */}
            <div
              className={`flex flex-1 flex-row items-center rounded border border-solid border-[#CFCFCF] p-2 px-3 text-gray1-100 
              transition-all focus-within:border-blue-500 ${
                errors["fema_no"] && "border-red-500 text-red-500"
              }`}
            >
              <div className="flex flex-1 flex-col items-start gap-1">
                <p className="text-[#8F8F8F]">FEMA No.</p>
                <input
                  type="text"
                  {...register("fema_no", {
                    required: "FEMA number is required.",
                  })}
                  className="w-full border-none bg-transparent text-sm text-gray1-100 outline-none"
                  placeholder="Input FEMA no."
                />
              </div>
            </div>

            {/* Disaster No. */}
            <div
              className={`flex flex-1 flex-row items-center rounded border border-solid border-[#CFCFCF] p-2 px-3 text-gray1-100 
              transition-all focus-within:border-blue-500 ${
                errors["contract_no"] && "border-red-500 text-red-500"
              }`}
            >
              <div className="flex flex-1 flex-col items-start gap-1">
                <p className="text-[#8F8F8F]">Disaster No.</p>
                <input
                  type="text"
                  {...register("contract_no", {
                    required: "Contract number is required.",
                  })}
                  className="w-full border-none bg-transparent text-sm text-gray1-100 outline-none"
                  placeholder="Input contract no."
                />
              </div>
            </div>
          </div>
        </form>
      </Modal>
    ) : null;
  };

  const renderCaseDetails = useMemo(() => {
    if (!sessionAccessedUserId) {
      return <></>;
    }
    return (
      <>
        <EditPersonalInformationModal
          setEditPersonalInformationModal={setEditPersonalInformationModal}
          editPersonalInformationModal={editPersonalInformationModal}
          citizenData={citizenData}
          accessedUserId={sessionAccessedUserId}
          disaster={disaster}
          fema_number={fema_number}
          disaster_number={disaster_number}
        />
        <div className=" mb-4 flex w-full justify-between pt-2.5">
          <div className="text-3xl">
            <button
              className="cursor-pointer bg-transparent"
              onClick={onVisitSurvivors}
            >
              <p className="text-[16px] font-bold">{clientName || ""}</p>
            </button>
            {caseNumber && (
              <span className="text-[#525252]">
                {" "}
                (Case No. {caseNumber || "10000102"})
              </span>
            )}
            <div className="mb-2 mt-1 flex w-full flex-row items-center gap-4">
              <div className="flex items-center gap-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_9807_680"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                  >
                    <rect width="24" height="24" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_9807_680)">
                    <path
                      d="M4 22.0031C3.71667 22.0031 3.47917 21.9073 3.2875 21.7156C3.09583 21.524 3 21.2865 3 21.0031V17.0031C3 16.4531 3.19583 15.9823 3.5875 15.5906C3.97917 15.199 4.45 15.0031 5 15.0031H19C19.55 15.0031 20.0208 15.199 20.4125 15.5906C20.8042 15.9823 21 16.4531 21 17.0031V21.0031C21 21.2865 20.9042 21.524 20.7125 21.7156C20.5208 21.9073 20.2833 22.0031 20 22.0031H4ZM5 13.0031V10.0031C5 9.45313 5.19583 8.98229 5.5875 8.59063C5.97917 8.19896 6.45 8.00313 7 8.00313H11V6.55313C10.7 6.35313 10.4583 6.11146 10.275 5.82813C10.0917 5.54479 10 5.20313 10 4.80313C10 4.55313 10.05 4.30729 10.15 4.06563C10.25 3.82396 10.4 3.60313 10.6 3.40313L11.65 2.35313C11.6833 2.31979 11.8 2.26979 12 2.20312C12.0333 2.20312 12.15 2.25312 12.35 2.35313L13.4 3.40313C13.6 3.60313 13.75 3.82396 13.85 4.06563C13.95 4.30729 14 4.55313 14 4.80313C14 5.20313 13.9083 5.54479 13.725 5.82813C13.5417 6.11146 13.3 6.35313 13 6.55313V8.00313H17C17.55 8.00313 18.0208 8.19896 18.4125 8.59063C18.8042 8.98229 19 9.45313 19 10.0031V13.0031H5Z"
                      fill="#770C0E"
                    />
                  </g>
                </svg>
                {birthDate && (
                  <span className="w-[260px]">
                    {new Date(birthDate).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_9807_685"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                  >
                    <rect width="24" height="24" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_9807_685)">
                    <path
                      d="M10.5 20H13.5C13.6333 20 13.75 19.95 13.85 19.85C13.95 19.75 14 19.6333 14 19.5C14 19.3667 13.95 19.25 13.85 19.15C13.75 19.05 13.6333 19 13.5 19H10.5C10.3667 19 10.25 19.05 10.15 19.15C10.05 19.25 10 19.3667 10 19.5C10 19.6333 10.05 19.75 10.15 19.85C10.25 19.95 10.3667 20 10.5 20ZM7 23C6.45 23 5.97917 22.8042 5.5875 22.4125C5.19583 22.0208 5 21.55 5 21V3C5 2.45 5.19583 1.97917 5.5875 1.5875C5.97917 1.19583 6.45 1 7 1H17C17.55 1 18.0208 1.19583 18.4125 1.5875C18.8042 1.97917 19 2.45 19 3V21C19 21.55 18.8042 22.0208 18.4125 22.4125C18.0208 22.8042 17.55 23 17 23H7ZM7 16H17V6H7V16Z"
                      fill="#770C0E"
                    />
                  </g>
                </svg>

                <span className="w-[100px]">{mobileNumber || "321-11-111"}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2ZM10 8.825C10.0833 8.825 10.1708 8.8125 10.2625 8.7875C10.3542 8.7625 10.4417 8.725 10.525 8.675L17.6 4.25C17.7333 4.16667 17.8333 4.0625 17.9 3.9375C17.9667 3.8125 18 3.675 18 3.525C18 3.19167 17.8583 2.94167 17.575 2.775C17.2917 2.60833 17 2.61667 16.7 2.8L10 7L3.3 2.8C3 2.61667 2.70833 2.6125 2.425 2.7875C2.14167 2.9625 2 3.20833 2 3.525C2 3.69167 2.03333 3.8375 2.1 3.9625C2.16667 4.0875 2.26667 4.18333 2.4 4.25L9.475 8.675C9.55833 8.725 9.64583 8.7625 9.7375 8.7875C9.82917 8.8125 9.91667 8.825 10 8.825Z"
                    fill="#770C0E"
                  />
                </svg>
                <span>{email || "antonkierhermo@gmail.com"}</span>
              </div>
            </div>
            <div className="mb-2 ml-1 flex w-full flex-row items-center gap-4">
              <div className="flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H3V1C3 0.716667 3.09583 0.479167 3.2875 0.2875C3.47917 0.0958333 3.71667 0 4 0C4.28333 0 4.52083 0.0958333 4.7125 0.2875C4.90417 0.479167 5 0.716667 5 1V2H13V1C13 0.716667 13.0958 0.479167 13.2875 0.2875C13.4792 0.0958333 13.7167 0 14 0C14.2833 0 14.5208 0.0958333 14.7125 0.2875C14.9042 0.479167 15 0.716667 15 1V2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V8H2V18ZM9 12C8.71667 12 8.47917 11.9042 8.2875 11.7125C8.09583 11.5208 8 11.2833 8 11C8 10.7167 8.09583 10.4792 8.2875 10.2875C8.47917 10.0958 8.71667 10 9 10C9.28333 10 9.52083 10.0958 9.7125 10.2875C9.90417 10.4792 10 10.7167 10 11C10 11.2833 9.90417 11.5208 9.7125 11.7125C9.52083 11.9042 9.28333 12 9 12ZM5 12C4.71667 12 4.47917 11.9042 4.2875 11.7125C4.09583 11.5208 4 11.2833 4 11C4 10.7167 4.09583 10.4792 4.2875 10.2875C4.47917 10.0958 4.71667 10 5 10C5.28333 10 5.52083 10.0958 5.7125 10.2875C5.90417 10.4792 6 10.7167 6 11C6 11.2833 5.90417 11.5208 5.7125 11.7125C5.52083 11.9042 5.28333 12 5 12ZM13 12C12.7167 12 12.4792 11.9042 12.2875 11.7125C12.0958 11.5208 12 11.2833 12 11C12 10.7167 12.0958 10.4792 12.2875 10.2875C12.4792 10.0958 12.7167 10 13 10C13.2833 10 13.5208 10.0958 13.7125 10.2875C13.9042 10.4792 14 10.7167 14 11C14 11.2833 13.9042 11.5208 13.7125 11.7125C13.5208 11.9042 13.2833 12 13 12ZM9 16C8.71667 16 8.47917 15.9042 8.2875 15.7125C8.09583 15.5208 8 15.2833 8 15C8 14.7167 8.09583 14.4792 8.2875 14.2875C8.47917 14.0958 8.71667 14 9 14C9.28333 14 9.52083 14.0958 9.7125 14.2875C9.90417 14.4792 10 14.7167 10 15C10 15.2833 9.90417 15.5208 9.7125 15.7125C9.52083 15.9042 9.28333 16 9 16ZM5 16C4.71667 16 4.47917 15.9042 4.2875 15.7125C4.09583 15.5208 4 15.2833 4 15C4 14.7167 4.09583 14.4792 4.2875 14.2875C4.47917 14.0958 4.71667 14 5 14C5.28333 14 5.52083 14.0958 5.7125 14.2875C5.90417 14.4792 6 14.7167 6 15C6 15.2833 5.90417 15.5208 5.7125 15.7125C5.52083 15.9042 5.28333 16 5 16ZM13 16C12.7167 16 12.4792 15.9042 12.2875 15.7125C12.0958 15.5208 12 15.2833 12 15C12 14.7167 12.0958 14.4792 12.2875 14.2875C12.4792 14.0958 12.7167 14 13 14C13.2833 14 13.5208 14.0958 13.7125 14.2875C13.9042 14.4792 14 14.7167 14 15C14 15.2833 13.9042 15.5208 13.7125 15.7125C13.5208 15.9042 13.2833 16 13 16Z"
                    fill="#770C0E"
                  />
                </svg>
                <span>Screening Date:</span>
                {birthDate && (
                  <span className="w-[136px]">
                    {new Date(birthDate).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <mask
                    id="mask0_13060_67"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                    style={{ maskType: "alpha" }}
                  >
                    <rect width="24" height="24" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_13060_67)">
                    <path
                      d="M6 20C5.71667 20 5.47917 19.9042 5.2875 19.7125C5.09583 19.5208 5 19.2833 5 19C5 18.7167 5.09583 18.4792 5.2875 18.2875C5.47917 18.0958 5.71667 18 6 18H18C18.2833 18 18.5208 18.0958 18.7125 18.2875C18.9042 18.4792 19 18.7167 19 19C19 19.2833 18.9042 19.5208 18.7125 19.7125C18.5208 19.9042 18.2833 20 18 20H6ZM6.7 16.5C6.21667 16.5 5.7875 16.3417 5.4125 16.025C5.0375 15.7083 4.80833 15.3083 4.725 14.825L3.725 8.475C3.69167 8.475 3.65417 8.47917 3.6125 8.4875C3.57083 8.49583 3.53333 8.5 3.5 8.5C3.08333 8.5 2.72917 8.35417 2.4375 8.0625C2.14583 7.77083 2 7.41667 2 7C2 6.58333 2.14583 6.22917 2.4375 5.9375C2.72917 5.64583 3.08333 5.5 3.5 5.5C3.91667 5.5 4.27083 5.64583 4.5625 5.9375C4.85417 6.22917 5 6.58333 5 7C5 7.11667 4.9875 7.225 4.9625 7.325C4.9375 7.425 4.90833 7.51667 4.875 7.6L8 9L11.125 4.725C10.9417 4.59167 10.7917 4.41667 10.675 4.2C10.5583 3.98333 10.5 3.75 10.5 3.5C10.5 3.08333 10.6458 2.72917 10.9375 2.4375C11.2292 2.14583 11.5833 2 12 2C12.4167 2 12.7708 2.14583 13.0625 2.4375C13.3542 2.72917 13.5 3.08333 13.5 3.5C13.5 3.75 13.4417 3.98333 13.325 4.2C13.2083 4.41667 13.0583 4.59167 12.875 4.725L16 9L19.125 7.6C19.0917 7.51667 19.0625 7.425 19.0375 7.325C19.0125 7.225 19 7.11667 19 7C19 6.58333 19.1458 6.22917 19.4375 5.9375C19.7292 5.64583 20.0833 5.5 20.5 5.5C20.9167 5.5 21.2708 5.64583 21.5625 5.9375C21.8542 6.22917 22 6.58333 22 7C22 7.41667 21.8542 7.77083 21.5625 8.0625C21.2708 8.35417 20.9167 8.5 20.5 8.5C20.4667 8.5 20.4292 8.49583 20.3875 8.4875C20.3458 8.47917 20.3083 8.475 20.275 8.475L19.275 14.825C19.1917 15.3083 18.9625 15.7083 18.5875 16.025C18.2125 16.3417 17.7833 16.5 17.3 16.5H6.7Z"
                      fill="#770C0E"
                    />
                  </g>
                </svg>

                <span>Tier: 1</span>
              </div>
            </div>
          </div>
          <div className="mr-3 flex items-center space-x-3">
            <div>
              <div className="flex gap-1">
                <p className="w-28 text-right text-[14px] text-[#525252]">
                  Disaster No.
                </p>
                <p className="text-[14px] text-[#525252]">
                  {disaster_number || "10000108"}
                </p>
              </div>
              <div className="flex gap-1">
                <p className="w-28 text-right text-[14px] text-[#525252]">
                  FEMA No.
                </p>
                <p className="text-[14px] text-[#525252]">
                  {fema_number || "10000109"}
                </p>
              </div>
              <div className="flex gap-1">
                <p className="w-28 text-right text-[14px] text-[#525252]">
                  Response Date.
                </p>
                <p className="text-[14px] text-[#525252]">
                  {responseDate
                    ? format(
                        new Date(responseDate.toDate()),
                        "MM/dd/yyyy hh:mm a"
                      )
                    : ""}
                </p>
              </div>
            </div>

            <button
              className="h-[24px] w-[24px] cursor-pointer"
              onClick={() => setEditPersonalInformationModal(true)}
            >
              <img src={editIcon} className="h-full w-full object-contain" />
            </button>
          </div>
        </div>
      </>
    );
  }, [
    clientName,
    caseNumber,
    birthDate,
    mobileNumber,
    email,
    toggleCaseDetailsModal,
    editPersonalInformationModal,
    sessionAccessedUserId,
    onVisitSurvivors,
    setEditPersonalInformationModal,
  ]);

  return (
    <div className="h-full w-full">
      <ScreeningNavbar setToggleSidebar={setToggleSidebar} />
      <div className="flex h-[92vh] w-full overflow-y-auto">
        <div
          className={`flex flex-col items-center space-y-5 overflow-y-auto border-r border-solid border-[#CFCFCF] px-[8px] py-[20px] transition-all duration-75 ease-in-out ${
            toggleSidebar ? "w-[335px]" : "w-[80px]"
          }`}
        >
          <div className={`h-[100px] ${toggleSidebar ? "w-[100px]" : ""}`}>
            <img
              src={blueSkyTransparentIcon}
              className={`h-full w-full object-contain transition-all ease-in-out ${
                toggleSidebar ? "scale-100" : "scale-90"
              }`}
            />
          </div>
          <div className="flex w-full flex-1 flex-col justify-between">
            {renderSidebar}
          </div>
        </div>
        <div className="flex h-full w-full flex-col overflow-y-auto">
          {selectedTab !== "dashboard" && (
            <div className="mx-5 pt-2.5">{renderCaseDetails}</div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

export default ScreeningLayout;